/**
 * This file sets up the main application component and routing for the MAX Aspire frontend.
 * It uses `react-router-dom` to define the routes and their corresponding components.
 * https://reactrouter.com/en/main/routers/create-browser-router
 *
 * Routes:
 * - `/`: Root layout with nested routes
 *   - `/`: Home page
 *   - `/about`: About page
 *   - `/profile`: Profile page
 *   - `/dashboard`: Dashboard page
 *   - `/profile-setup`: Profile setup page
 *   - `/education`: Education page
 *   - `/discover`: Discover page
 *   - `/connect`: Connect page
 *   - `/join`: Join page
 *   - `/newsfeed`: News feed page
 *   - `/admin`: Admin dashboard
 *     - `/admin/accounts`: Admin accounts page
 *     - `/admin/user-details/:userId`: Admin user details page
 *   - `/auth`: Authentication page
 *
 * Components:
 * - `RootLayout`: The main layout component for the application.
 * - `Error`: The error page component.
 *
 * The `App` component initializes the router and provides it to the application.
 */

import React, { useEffect, useState } from "react";
import { createBrowserRouter, RouterProvider, useLocation } from "react-router-dom";
import Home from "./pages/Home/Home";
import About from "./pages/About";
import Dashboard from "./pages/Dashboard";
import AuthPage from "./pages/Auth/AuthPage";
import ProfileSetup, { ProfileSetupAction } from "./pages/ProfileSetup/ProfileSetup";
import Discover from "./pages/Discover/Discover";
import Connect from "./pages/Connect/Connect";
import Join from "./pages/Join/Join";
// import AdminDashboard from "./pages/Admin/Dashboard/Dashboard"; // Comment out or remove old dashboard
import Accounts from "./pages/Admin/Accounts/Accounts.jsx";
import AdminUserDetails from "./pages/Admin/UserDetails/UserDetails.jsx";
import AdminPage from "./pages/Admin/AdminPage"; // Import the new AdminPage component
import Profile from "./pages/Profile/Profile.tsx";
import Education from "./pages/Education/Education.tsx";
import NewsFeed from "./pages/NewsFeed/Newsfeed.js";
import RootLayout from "./components/layout/Root.tsx";
import Error from "./pages/error/ErrorPage.tsx";
import PrivateRoute from "./components/layout/PrivateRoute.tsx";
import { useAppDispatch } from "./store/hook";
import { initAuth } from "./store/slices/authSlice";
import { isTokenValid, restoreAuth, getUserFromToken } from "./utils/tokenUtils";
import { fetchProfilePhoto } from "./store/slices/profileSlice";

// Import Profile Setup components
import PostDetail from "./pages/PostDetail/PostDetail.tsx";

// Import Landing Page
import LandingPage from "./pages/LandingPage/LandingPage.tsx";

import TermsOfUse from "./pages/TermsOfUse/TermsOfUse";
import PrivacySecurity from "./pages/PrivacySecurity/PrivacySecurity";
import Help from "./pages/Help/Help";
import Settings from "./pages/Settings/Settings.tsx";
import MessagingPage from "./pages/Messaging/MessagingPage";
import Notifications from "./pages/Notifications/Notifications";
import InitialProfileSetupPage from "./pages/InitialProfileSetup/InitialProfileSetupPage";
import LaunchingSoon from "./pages/LaunchingSoon/LaunchingSoon"; // Import the new component
import { BackgroundProvider } from "./context/BackgroundContext"; // Updated import path
import UserDirectoryPage from "./pages/UserDirectory/UserDirectoryPage"; // Import UserDirectoryPage
import SearchResultsPage from "./pages/SearchResultsPage/SearchResultsPage";
import EventsPage from "./pages/Events/EventsPage"; // Fixed import path
import { trackPageView } from "./utils/analytics";

// Create a wrapper component to track route changes
const RouteChangeTracker = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();

    useEffect(() => {
        trackPageView(location.pathname);
    }, [location]);

    return <>{children}</>;
};

// Create a wrapper component that includes both the router and route tracking
const AppWithRouter = () => {
    return (
        <RouterProvider
            router={createBrowserRouter([
                // Route for the Landing Page (outside RootLayout)
                {
                    path: "/landing-page",
                    element: <LandingPage />,
                    errorElement: <Error />,
                },
                // Route for the Launching Soon Page (outside RootLayout)
                {
                    path: "/launching-soon",
                    element: <LaunchingSoon />,
                    errorElement: <Error />,
                },
                {
                    path: "/",
                    element: <LandingPage />,
                    errorElement: <Error />,
                },
                // Main application routes with RootLayout
                {
                    path: "/",
                    element: (
                        <RouteChangeTracker>
                            <RootLayout />
                        </RouteChangeTracker>
                    ),
                    errorElement: <Error />,
                    children: [
                        // Public routes within RootLayout
                        { path: "auth", element: <AuthPage /> },
                        { path: "terms-of-use", element: <TermsOfUse /> },
                        { path: "privacy-security", element: <PrivacySecurity /> },
                        { path: "help", element: <Help /> },

                        // Protected Routes within RootLayout
                        {
                            element: <PrivateRoute />,
                            children: [
                                { path: "about", element: <About /> },
                                // { path: "home", element: <Home /> },
                                { path: "home", element: <LandingPage /> },
                                { path: "profile/:userId", element: <Profile /> },
                                { path: "dashboard", element: <Dashboard /> },
                                // {
                                //     path: "profile-setup",
                                //     element: <ProfileSetup />,
                                //     action: ProfileSetupAction,
                                // },
                                {
                                    path: "profile-initial-setup",
                                    element: <InitialProfileSetupPage />,
                                },
                                { path: "education", element: <Education /> },
                                { path: "discover", element: <Discover /> },
                                { path: "connect", element: <Connect /> },
                                { path: "join", element: <Join /> },
                                { path: "newsfeed", element: <NewsFeed /> },
                                { path: "newsfeed/:postId", element: <PostDetail /> },
                                { path: "settings", element: <Settings /> },
                                { path: "messaging", element: <MessagingPage /> },
                                { path: "people", element: <UserDirectoryPage /> },
                                { path: "notifications", element: <Notifications /> },
                                { path: "search-results", element: <SearchResultsPage /> },
                                { path: "events", element: <EventsPage /> },
                                {
                                    path: "admin",
                                    element: <AdminPage />,
                                    children: [
                                        { path: "accounts", element: <Accounts /> },
                                        {
                                            path: "user-details/:userId",
                                            element: <AdminUserDetails />,
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ])}
        />
    );
};

const App = () => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const init = async () => {
            // First check for valid token and restore auth if possible
            if (isTokenValid()) {
                const success = await restoreAuth(dispatch);

                // After restoring auth, ensure profile photo is loaded
                if (success) {
                    const userInfo = getUserFromToken();
                    if (userInfo && userInfo.id) {
                        dispatch(fetchProfilePhoto(userInfo.id));
                    }
                } else {
                    dispatch(initAuth());
                }
            } else {
                dispatch(initAuth());
            }
            setLoading(false);
        };
        init();
    }, [dispatch]);

    if (loading) {
        return <div>Loading...</div>;
    }

    return <AppWithRouter />;
};

const WrappedApp = () => (
    <BackgroundProvider>
        <App />
    </BackgroundProvider>
);

export default WrappedApp;
